import React, { useState, useEffect } from 'react';
import { FaBuilding, FaUserCheck, FaChevronDown, FaCheck, FaPlus } from 'react-icons/fa';
import styles from './WorkspaceSwitcher.module.scss';
import axios from 'axios';
import storageService from '../../services/storageService';
import { baseUrl } from '../../constants/baseUrl';
import { syncJobWorkspaceCache } from '../../services/jobWorkspaceCacheService';

export const WorkspaceSwitcher = ({ onWorkspaceChange }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(
    localStorage.getItem('active_workspace_id') || ''
  );
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const fetchWorkspaces = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/rest/workspaces/my-workspaces`, {
        headers: {
          AuthAPI: `Bearer ${storageService.getToken()}`,
          Device: 'TMR',
        },
      });
      if (response.data?.data?.workspaces) {
        const workspacesList = response.data.data.workspaces;
        setWorkspaces(workspacesList);

        // If no workspace is selected, or a stored selection is no longer
        // accessible, reset to the personal workspace (or first available).
        const activeWorkspaceIsAvailable = workspacesList.some(
          (workspace) => String(workspace.id) === String(activeWorkspaceId)
        );
        if ((!activeWorkspaceId || !activeWorkspaceIsAvailable) && workspacesList.length > 0) {
          const personal = workspacesList.find((w) => w.workspace_type === 'personal');
          const defaultWs = personal || workspacesList[0];
          setActiveWorkspaceId(String(defaultWs.id));
          localStorage.setItem('active_workspace_id', String(defaultWs.id));
          syncJobWorkspaceCache(defaultWs.id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user workspaces', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWorkspace = (workspaceId) => {
    setActiveWorkspaceId(String(workspaceId));
    localStorage.setItem('active_workspace_id', String(workspaceId));
    syncJobWorkspaceCache(workspaceId);
    setIsOpen(false);

    if (onWorkspaceChange) {
      onWorkspaceChange(workspaceId);
    }

    // Refresh page or trigger context update so all data re-fetches
    window.location.reload();
  };

  const handleOpenCreateModal = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    setNewOrgName('');
    setCreateError('');
    setIsCreateModalOpen(true);
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      setCreateError('Organization name is required.');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    try {
      const response = await axios.post(
        `${baseUrl}/rest/workspaces/create-organization`,
        { name: newOrgName.trim() },
        {
          headers: {
            AuthAPI: `Bearer ${storageService.getToken()}`,
            Device: 'TMR',
          },
        }
      );

      if (response.data?.data?.workspace?.id) {
        const newWs = response.data.data.workspace;
        setActiveWorkspaceId(String(newWs.id));
        localStorage.setItem('active_workspace_id', String(newWs.id));
        syncJobWorkspaceCache(newWs.id);
        setIsCreateModalOpen(false);
        window.location.reload();
      } else {
        setCreateError(response.data?.messages || 'Failed to create organization.');
      }
    } catch (err) {
      console.error('Failed to create organization', err);
      // The backend sends the reason in `errors` as a string (e.g. already in an
      // organization) or as a list; don't show just its first character.
      const errors = err.response?.data?.errors;
      setCreateError(
        err.response?.data?.messages ||
          (typeof errors === 'string' ? errors : errors?.[0]) ||
          'Failed to create organization. Please try again.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  const activeWorkspace = workspaces.find(
    (w) => String(w.id) === String(activeWorkspaceId)
  ) || workspaces[0];

  if (!workspaces || workspaces.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className={styles.icon}>
          {activeWorkspace?.workspace_type === 'personal' ? (
            <FaUserCheck size={14} className={styles.personalIcon} />
          ) : (
            <FaBuilding size={14} className={styles.orgIcon} />
          )}
        </span>
        <span className={styles.name}>
          {activeWorkspace?.name || (activeWorkspace?.workspace_type === 'organization' || activeWorkspace?.workspace_type === 'organizational' ? 'TMR Organization' : 'Personal TMR')}
        </span>
        <FaChevronDown size={12} className={styles.caret} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>Workspaces</div>

          <div className={styles.groupLabel}>Personal TMR</div>
          {workspaces
            .filter((w) => w.workspace_type === 'personal')
            .map((w) => (
              <div
                key={w.id}
                className={`${styles.item} ${String(w.id) === String(activeWorkspaceId) ? styles.active : ''
                  }`}
                onClick={() => handleSelectWorkspace(w.id)}
              >
                <FaUserCheck className={styles.itemIcon} />
                <span className={styles.itemText}>{w.name}</span>
                {String(w.id) === String(activeWorkspaceId) && (
                  <FaCheck className={styles.checkIcon} />
                )}
              </div>
            ))}

          {workspaces.some((w) => w.workspace_type === 'organization' || w.workspace_type === 'organizational') && (
            <>
              <div className={styles.groupLabel}>TMR Organizations</div>
              {workspaces
                .filter((w) => w.workspace_type === 'organization' || w.workspace_type === 'organizational')
                .map((w) => (
                  <div
                    key={w.id}
                    className={`${styles.item} ${String(w.id) === String(activeWorkspaceId) ? styles.active : ''
                      }`}
                    onClick={() => handleSelectWorkspace(w.id)}
                  >
                    <FaBuilding className={styles.itemIcon} />
                    <span className={styles.itemText}>{w.name}</span>
                    {String(w.id) === String(activeWorkspaceId) && (
                      <FaCheck className={styles.checkIcon} />
                    )}
                  </div>
                ))}
            </>
          )}

          <button
            type="button"
            className={styles.createOrgBtn}
            onClick={handleOpenCreateModal}
          >
            <FaPlus size={11} />
            <span>Create Organization</span>
          </button>
        </div>
      )}

      {isCreateModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsCreateModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Create TMR Organization</h3>
            <p className={styles.modalSubtitle}>
              Set up a collaborative shared workspace for your team, hotel group, or recruiting agency. Your Personal TMR remains unchanged.
            </p>
            <form onSubmit={handleCreateOrganization}>
              <div className={styles.formGroup}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="e.g. Acme Hotels Group"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  disabled={isCreating}
                  autoFocus
                />
                {createError && <div className={styles.errorText}>{createError}</div>}
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setIsCreateModalOpen(false)}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isCreating || !newOrgName.trim()}
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

